import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

interface TourStep {
  target: string;
  title: string;
  content: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

interface Tour {
  id: string;
  name: string;
  role: string;
  view: string;
  steps: TourStep[];
}

interface OnboardingTourProps {
  view: string;
}

export const OnboardingTour: React.FC<OnboardingTourProps> = ({ view }) => {
  const { profile } = useAuth();
  const [tour, setTour] = useState<Tour | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [targetPosition, setTargetPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });

  useEffect(() => {
    if (profile) {
      loadTour();
    }
  }, [profile, view]);

  useEffect(() => {
    if (tour && isVisible && tour.steps[currentStep]) {
      updateTargetPosition();
      window.addEventListener('resize', updateTargetPosition);
      return () => window.removeEventListener('resize', updateTargetPosition);
    }
  }, [tour, currentStep, isVisible]);

  const loadTour = async () => {
    if (!profile) return;

    try {
      const { data: tours, error: tourError } = await supabase
        .from('onboarding_tours')
        .select('*')
        .eq('view', view)
        .eq('is_active', true)
        .in('role', [profile.role, 'both'])
        .limit(1);

      if (tourError) throw tourError;
      if (!tours || tours.length === 0) return;

      const tourData = tours[0];

      const { data: progress } = await supabase
        .from('user_tour_progress')
        .select('*')
        .eq('user_id', profile.id)
        .eq('tour_id', tourData.id)
        .single();

      if (!progress || !progress.completed) {
        setTour(tourData);
        setCurrentStep(progress?.current_step || 0);

        setTimeout(() => {
          setIsVisible(true);
        }, 500);
      }
    } catch (error) {
      console.error('Error loading tour:', error);
    }
  };

  const updateTargetPosition = () => {
    if (!tour || !tour.steps[currentStep]) return;

    const targetSelector = tour.steps[currentStep].target;
    const targetElement = document.querySelector(targetSelector);

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setTargetPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  };

  const handleNext = async () => {
    if (!tour || !profile) return;

    const nextStep = currentStep + 1;

    if (nextStep >= tour.steps.length) {
      await completeTour();
    } else {
      setCurrentStep(nextStep);
      await updateProgress(nextStep, false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    setIsVisible(false);
    setTour(null);
  };

  const handleClose = () => {
    setIsVisible(false);
    setTour(null);
  };

  const updateProgress = async (step: number, completed: boolean) => {
    if (!tour || !profile) return;

    try {
      await supabase
        .from('user_tour_progress')
        .upsert({
          user_id: profile.id,
          tour_id: tour.id,
          current_step: step,
          completed,
          completed_at: completed ? new Date().toISOString() : null,
        });
    } catch (error) {
      console.error('Error updating tour progress:', error);
    }
  };

  const completeTour = async () => {
    if (!tour || !profile) return;

    try {
      await supabase
        .from('user_tour_progress')
        .upsert({
          user_id: profile.id,
          tour_id: tour.id,
          current_step: tour.steps.length,
          completed: true,
          completed_at: new Date().toISOString(),
        });

      setIsVisible(false);
      setTour(null);
    } catch (error) {
      console.error('Error completing tour:', error);
    }
  };

  if (!tour || !isVisible || !tour.steps[currentStep]) return null;

  const step = tour.steps[currentStep];
  const placement = step.placement || 'bottom';

  const getTooltipPosition = () => {
    const offset = 16;
    let top = 0;
    let left = 0;

    switch (placement) {
      case 'top':
        top = targetPosition.top - offset;
        left = targetPosition.left + targetPosition.width / 2;
        break;
      case 'bottom':
        top = targetPosition.top + targetPosition.height + offset;
        left = targetPosition.left + targetPosition.width / 2;
        break;
      case 'left':
        top = targetPosition.top + targetPosition.height / 2;
        left = targetPosition.left - offset;
        break;
      case 'right':
        top = targetPosition.top + targetPosition.height / 2;
        left = targetPosition.left + targetPosition.width + offset;
        break;
    }

    return { top, left };
  };

  const tooltipPosition = getTooltipPosition();

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 cursor-pointer"
        onClick={handleClose}
      />

      <div
        className="fixed z-50"
        style={{
          top: targetPosition.top - 4,
          left: targetPosition.left - 4,
          width: targetPosition.width + 8,
          height: targetPosition.height + 8,
          boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.5)',
          borderRadius: '8px',
          transition: 'all 0.3s ease',
          pointerEvents: 'none',
        }}
      />

      <div
        className="fixed z-50 bg-white rounded-xl shadow-2xl p-6 max-w-sm"
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          transform: placement === 'top' || placement === 'bottom'
            ? 'translateX(-50%)'
            : placement === 'left'
            ? 'translate(-100%, -50%)'
            : 'translateY(-50%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="text-xs text-slate-500 mb-1">
              Step {currentStep + 1} of {tour.steps.length}
            </div>
            <h3 className="text-lg font-bold text-slate-900">{step.title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-slate-100 rounded transition-colors ml-2"
            aria-label="Close tour"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <p className="text-slate-700 mb-6">{step.content}</p>

        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-sm text-slate-600 hover:text-slate-900 font-medium"
          >
            Skip Tour
          </button>

          <div className="flex items-center space-x-2">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors flex items-center"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </button>
            )}

            <button
              onClick={handleNext}
              className="px-4 py-2 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors flex items-center"
            >
              {currentStep === tour.steps.length - 1 ? 'Finish' : 'Next'}
              {currentStep < tour.steps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
